/**
 * permissions.ts
 * TypeScript port of RoleManager.cs
 * Source: src/Presentation/Deloitte.DU.DigitalBadging.Web/ViewModels/RoleManager.cs
 *
 * 7 roles × 28 permissions — mirrors the server-side matrix exactly.
 */

// ---------------------------------------------------------------------------
// Roles (mirrors UserRole enum in Helper/SessionName.cs)
// ---------------------------------------------------------------------------
export const UserRole = {
  ApplicationAdmin:           'ApplicationAdmin',
  AdminCapacityManagement:    'AdminCapacityManagement',
  ViewerCapacityManagement:   'ViewerCapacityManagement',
  UserCapacityManagement:     'UserCapacityManagement',
  AdminAttendanceTracking:    'AdminAttendanceTracking',
  ViewerAttendanceTracking:   'ViewerAttendanceTracking',
  UserAttendanceTracking:     'UserAttendanceTracking',
} as const

export type UserRoleKey = typeof UserRole[keyof typeof UserRole]

// ---------------------------------------------------------------------------
// Permissions (mirrors Resources list in RoleManager.cs)
// ---------------------------------------------------------------------------
export const Permission = {
  // User management
  UserManagement:                     'UserManagement',
  UserManagementCapacityAdmin:        'UserManagementCapacityAdmin',
  UserManagementAttendanceAdmin:      'UserManagementAttendanceAdmin',
  // Capacity management
  DUStaffBadging:                     'DUStaffBadging',
  ViewerDUStaffBadging:               'ViewerDUStaffBadging',
  CapacityApplication:                'CapacityApplication',
  CapacityAddMealPeriod:              'CapacityAddMealPeriod',
  CapacityEditMealPeriod:             'CapacityEditMealPeriod',
  CapacityDeleteMealPeriod:           'CapacityDeleteMealPeriod',
  CapacityMealPeriodGridAction:       'CapacityMealPeriodGridAction',
  CapacityAddQueueConfiguration:      'CapacityAddQueueConfiguration',
  CapacityQueueGridAction:            'CapacityQueueGridAction',
  CapacityEditQueueGridAction:        'CapacityEditQueueGridAction',
  CapacityDeleteQueueGridAction:      'CapacityDeleteQueueGridAction',
  // Attendance tracking
  AttendanceTrackingApplication:      'AttendanceTrackingApplication',
  AttendanceTrackingBulkAction:       'AttendanceTrackingBulkAction',
  AttendanceTrackingEmailConfig:      'AttendanceTrackingEmailConfig',
  AttendanceTrackingTriggerEmail:     'AttendanceTrackingTriggerEmail',
  AttendanceTrackingBulkReportExport: 'AttendanceTrackingBulkReportExport',
  AttendanceTrackingGridAction:       'AttendanceTrackingGridAction',
} as const

export type PermissionKey = typeof Permission[keyof typeof Permission]

// ---------------------------------------------------------------------------
// Role → Permission map (mirrors rolePermissions dict in RoleManager.cs)
// ---------------------------------------------------------------------------
const ALL_PERMISSIONS = Object.values(Permission) as PermissionKey[]

const rolePermissions: Record<UserRoleKey, PermissionKey[]> = {
  [UserRole.ApplicationAdmin]: ALL_PERMISSIONS,

  [UserRole.AdminCapacityManagement]: [
    Permission.CapacityApplication,
    Permission.UserManagement,
    Permission.UserManagementCapacityAdmin,
    Permission.DUStaffBadging,
    Permission.CapacityAddMealPeriod,
    Permission.CapacityEditMealPeriod,
    Permission.CapacityDeleteMealPeriod,
    Permission.CapacityMealPeriodGridAction,
    Permission.CapacityAddQueueConfiguration,
    Permission.CapacityQueueGridAction,
    Permission.CapacityEditQueueGridAction,
    Permission.CapacityDeleteQueueGridAction,
  ],

  [UserRole.ViewerCapacityManagement]: [
    Permission.CapacityApplication,
    Permission.DUStaffBadging,
    Permission.ViewerDUStaffBadging,
  ],

  [UserRole.UserCapacityManagement]: [
    Permission.CapacityApplication,
    Permission.DUStaffBadging,
    Permission.CapacityAddMealPeriod,
    Permission.CapacityEditMealPeriod,
    Permission.CapacityDeleteMealPeriod,
    Permission.CapacityMealPeriodGridAction,
    Permission.CapacityAddQueueConfiguration,
    Permission.CapacityQueueGridAction,
    Permission.CapacityEditQueueGridAction,
    Permission.CapacityDeleteQueueGridAction,
  ],

  [UserRole.AdminAttendanceTracking]: [
    Permission.AttendanceTrackingApplication,
    Permission.UserManagement,
    Permission.UserManagementAttendanceAdmin,
    Permission.AttendanceTrackingBulkAction,
    Permission.AttendanceTrackingEmailConfig,
    Permission.AttendanceTrackingTriggerEmail,
    Permission.AttendanceTrackingBulkReportExport,
    Permission.AttendanceTrackingGridAction,
  ],

  [UserRole.ViewerAttendanceTracking]: [
    Permission.AttendanceTrackingApplication,
  ],

  [UserRole.UserAttendanceTracking]: [
    Permission.AttendanceTrackingApplication,
    Permission.AttendanceTrackingBulkAction,
    Permission.AttendanceTrackingEmailConfig,
    Permission.AttendanceTrackingTriggerEmail,
    Permission.AttendanceTrackingBulkReportExport,
    Permission.AttendanceTrackingGridAction,
  ],
}

// ---------------------------------------------------------------------------
// Public API  (mirrors RoleManager static methods)
// ---------------------------------------------------------------------------

/** Returns true when ANY of the provided roles grants the requested permission. */
export function userHasPermission(roles: string[], permission: PermissionKey): boolean {
  return roles.some(
    (role) =>
      role in rolePermissions &&
      rolePermissions[role as UserRoleKey].includes(permission),
  )
}

/** Returns the full permission list for a single role. */
export function getPermissionsForRole(role: UserRoleKey): PermissionKey[] {
  return rolePermissions[role] ?? []
}

/** Returns all defined role names. */
export function getAllRoles(): UserRoleKey[] {
  return Object.keys(rolePermissions) as UserRoleKey[]
}
